import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Home, Plus, X, Users, ArrowRightLeft, Settings, Trash2,
  Diamond, Lock, Eye, EyeOff, BarChart3, TrendingUp, 
  UserPlus, LogOut, Crown, AlertCircle, Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Hub = ({ user }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [maxAccounts, setMaxAccounts] = useState(5);
  const [remainingSlots, setRemainingSlots] = useState(5);
  
  // Add account form
  const [addForm, setAddForm] = useState({ email: '', password: '', nickname: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // Check if user has Supremium
  const isSupremium = user?.subscription_tier === 'supremium' || user?.is_admin;

  useEffect(() => {
    if (isSupremium) {
      fetchAccounts();
      fetchStats();
    }
  }, [isSupremium]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/hub/accounts`, { withCredentials: true });
      setAccounts(response.data.accounts || []);
      setMaxAccounts(response.data.max_accounts || 5);
      setRemainingSlots(response.data.remaining_slots || 5);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load hub accounts');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/hub/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const addAccount = async () => {
    if (!addForm.email || !addForm.password) {
      toast.error('Email and password are required');
      return;
    }
    
    setAdding(true);
    try {
      await axios.post(`${API_URL}/hub/accounts`, addForm, { withCredentials: true });
      toast.success('Account added to your hub!');
      setShowAddModal(false);
      setAddForm({ email: '', password: '', nickname: '' });
      fetchAccounts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add account');
    } finally {
      setAdding(false);
    }
  };

  const removeAccount = async (hubAccountId) => {
    if (!confirm('Remove this account from your hub?')) return;
    
    try {
      await axios.delete(`${API_URL}/hub/accounts/${hubAccountId}`, { withCredentials: true });
      toast.success('Account removed from hub');
      fetchAccounts();
      fetchStats();
    } catch (error) {
      toast.error('Failed to remove account');
    }
  };

  const switchAccount = async (hubAccountId) => {
    try {
      const response = await axios.post(
        `${API_URL}/hub/accounts/${hubAccountId}/switch`,
        {},
        { withCredentials: true }
      );
      toast.success(`Switching to ${response.data.target_user.name || response.data.target_user.username}...`);
      // In a real implementation, this would handle the account switch
      // For now, just show a message
      setTimeout(() => {
        toast.info('Account switch feature coming soon! For now, please log out and log in with the other account.');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch account');
    }
  };

  // Non-Supremium users see upgrade prompt
  if (!isSupremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center mx-auto mb-6">
              <Diamond className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white mb-4">
              Account Hub
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              Manage up to 5 accounts from one dashboard with <span className="text-yellow-600 font-bold">Supremium Verified</span>
            </p>
            
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-yellow-200 dark:border-yellow-800 mb-8">
              <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Hub Features Include:</h3>
              <ul className="space-y-3 text-left max-w-md mx-auto">
                {[
                  'Link up to 5 accounts to your hub',
                  'Quick switch between accounts',
                  'Combined analytics dashboard',
                  'Unified notifications',
                  'Cross-account content sharing'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-yellow-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            <Button
              onClick={() => navigate('/pricing')}
              className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold px-8 h-14 text-lg hover:scale-105 transition-transform"
              data-testid="upgrade-to-supremium-btn"
            >
              <Diamond className="w-5 h-5 mr-2" />
              Upgrade to Supremium - $49/month
            </Button>
          </motion.div>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-yellow-200 dark:border-yellow-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  Account Hub
                  <Diamond className="w-5 h-5 text-yellow-500" />
                </h1>
                <p className="text-sm text-gray-500">{accounts.length} of {maxAccounts} accounts linked</p>
              </div>
            </div>
            
            {remainingSlots > 0 && (
              <Button
                onClick={() => setShowAddModal(true)}
                className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold"
                data-testid="add-account-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Combined Stats */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                  <Users className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_accounts}</p>
                  <p className="text-sm text-gray-500">Accounts</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.combined_followers?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Total Followers</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <UserPlus className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.combined_following?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Total Following</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.combined_posts?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Total Posts</p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Linked Accounts */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Linked Accounts</h2>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <Card className="p-8 text-center bg-white dark:bg-gray-800 rounded-2xl border-dashed border-2 border-yellow-300 dark:border-yellow-700">
            <Users className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No accounts linked yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Link your other Socyy accounts to manage them all from here
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Link Your First Account
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {accounts.map((account, idx) => (
              <motion.div
                key={account.hub_account_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-600 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar className="w-14 h-14 border-2 border-yellow-400">
                        <AvatarImage src={account.user?.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-yellow-400 to-amber-500 text-white text-lg">
                          {account.nickname?.charAt(0) || account.user?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            {account.nickname || account.user?.name}
                          </h3>
                          {account.user?.is_verified && (
                            <Crown className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-500">@{account.user?.username}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>{account.user?.followers_count || 0} followers</span>
                          <span>{account.user?.posts_count || 0} posts</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => switchAccount(account.hub_account_id)}
                        className="rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400"
                        data-testid={`switch-to-${account.user?.username}`}
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-1" />
                        Switch
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeAccount(account.hub_account_id)}
                        className="rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Slots indicator */}
        {accounts.length > 0 && remainingSlots > 0 && (
          <p className="text-center text-sm text-gray-500 mt-6">
            You can link {remainingSlots} more account{remainingSlots !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Add Account Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md p-6 shadow-2xl"
              data-testid="add-account-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Link Account</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Enter the login credentials for the account you want to add to your hub. 
                  The account must already exist on Socyy.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Email</label>
                  <Input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="account@example.com"
                    className="rounded-xl"
                    data-testid="hub-email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={addForm.password}
                      onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                      placeholder="••••••••"
                      className="rounded-xl pr-10"
                      data-testid="hub-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Nickname (optional)</label>
                  <Input
                    value={addForm.nickname}
                    onChange={(e) => setAddForm({ ...addForm, nickname: e.target.value })}
                    placeholder="Personal, Business, etc."
                    className="rounded-xl"
                    data-testid="hub-nickname-input"
                  />
                </div>
                
                <Button
                  onClick={addAccount}
                  disabled={adding || !addForm.email || !addForm.password}
                  className="w-full rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold h-12 mt-4"
                  data-testid="link-account-submit-btn"
                >
                  {adding ? 'Linking Account...' : 'Link Account'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default Hub;
